import { useEffect, useMemo, useState } from "react";
import { MapContainer } from "react-leaflet";
import GeotabApi from "mg-api-js";
import { useLoaderData, useSearchParams } from "react-router-dom";
import { useUiStore } from "src/components/main/uiStore";
//--------------------------------------------------------------------------------------------
import { useAuthStore } from "src/components/main/authStore";
import {
    GeotabApiAuthentication, GeotabApiOptions
} from "src/components/common/functions";
import MapLayers from "./map";
import type { MapsLoaderData, MapTruck, MapAssignment, MapWorkRow } from "./types";

type GeotabStatusInfo = {
    device: { id: string };
    latitude?: number;
    longitude?: number;
    isDriving?: boolean;
};
//--------------------------------------------------------------------------------------------
export default function LoadMaps(){
    const { selectedDate, works: loadedWorks } = useLoaderData() as MapsLoaderData;
    const [searchParams, setSearchParams] = useSearchParams();
    const mapFull = useUiStore(state => state.mapFull);
    const user = useAuthStore(state => state.user);
    const [trucks, setTrucks] = useState<MapTruck[]>([]);
    const [showTruckLoading, setShowTruckLoading] = useState(false);
    const works = useMemo<MapWorkRow[]>(() => (loadedWorks ?? [])
    .map((w: MapWorkRow) => ({
        ...w, 
        projectName: `${w.projectNumber} - ${w.organizationName} - ${w.clientProjectNumber}`,
        assignments: (w.assignments ?? []).filter((value: MapAssignment, index: number, self: MapAssignment[]) => index === self.findIndex((t: MapAssignment) => {
            const assignmentKey = value.assignmentId || `${value.workId}-${value.userId}`;
            const compareKey = t.assignmentId || `${t.workId}-${t.userId}`;
            return compareKey === assignmentKey;
        }))
    })) || [], [loadedWorks]);
    //--------------------------------------------------------------------------------------------
    useEffect(() => {
        let isMounted = true;
        const getData = async () => {            
            const api = new GeotabApi(GeotabApiAuthentication, GeotabApiOptions);
            //------------------------------------------------------
            const calls = [
                ['Get', {
                typeName: 'Device',
                // "search": {
                //     "name": "131"
                // }
                }],
                ['Get', {
                    typeName: 'DeviceStatusInfo',
                    // search: {
                    //     deviceSearch: { id: "b2F" },
                    //   },
                }],
                ['Get', {
                    typeName: 'StatusData',
                    resultsLimit: 100,
                    "search": {
                        diagnosticSearch: { id: "DiagnosticOdometerId" },
                        "deviceSearch": { "id": "b2F" },
                        fromDate: "2023-01-24T18:20:00.000Z"
                    }
                }]
            ];
            setShowTruckLoading(true);
            const res = await api.multiCall(calls);
            //---------------
            if(!isMounted) return;
            const result = res.data.result as [MapTruck[], GeotabStatusInfo[]];
            const eventData = Object.fromEntries(result[1].map((r: GeotabStatusInfo) => [r.device.id, {
                latitude: r.latitude,
                longitude: r.longitude,
                isDriving: r.isDriving,
            }]));
            const data = result[0].map((r: MapTruck) => ({...r, eventData: eventData[r.id]}));
            setTrucks(data);
            setShowTruckLoading(false);
            //------------------------------------------------------
        }
        if(!trucks.length) getData();
        return () => {
            isMounted = false;
            setShowTruckLoading(false);
        }
    }, [trucks.length])
    //--------------------------------------------------------------------------------------------
    return(
        <div className = "cssContent mapsContent">
            <nav className = "mapsToolbar my-2 d-flex flex-wrap align-items-end gap-3">
                <div>
                    <label className = "small text-muted fw-bold me-2" htmlFor = "mapSelectedDate">Date</label>
                    <input
                        id = "mapSelectedDate"
                        className = "form-control w-auto inline"
                        type = "date"
                        min = "1900-01-01"
                        max = "2999-12-31"
                        value = {selectedDate}
                        onChange = {e => {
                            const params = new URLSearchParams(searchParams);
                            params.set("selectedDate", e.currentTarget.value);
                            setSearchParams(params);
                        }}
                    />
                </div>
                {showTruckLoading?<span className = "text-muted">Loading trucks...</span>:null}
            </nav>
            <div className = "mapsViewport">
                <MapContainer center = {user?.coords ?? [37.6148754, -122.0874473]} zoom = {12} scrollWheelZoom = {true} zoomControl = {false}>
                    <MapLayers
                        geoData = {trucks}
                        mapFull = {mapFull}
                        works = {works} 
                    />
                </MapContainer>
            </div>
        </div>
    );
}
